"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { ObjectStorage, useData } from "@/storage/accessors";
import { format } from "date-fns";
import { computeRoleEfficiency } from "../lifepart/lifeParts";
import useActualWheelId from "@/hooks/useActualWheelId";
import { DayPlan } from "@/state/DayPlan";
import { LifePart } from "@/state/LifePart";
import { Tracker } from "@/state/Tracker";
import { RoleEfficiencySnapshot } from "@/state/RoleEfficiencySnapshot";

const INTERVAL_MS = 3 * 60 * 1000; // 3 минуты

/**
 * Периодически вычисляет эффективность всех ролей и обновляет снепшот на сегодня.
 * Работает как middleware — монтируется один раз на уровне layout.
 */
export default function SnapshotMiddleware() {
    const { roles, plans, trackers, roleEfficiencySnapshots } = useData();
    const rolesRef = useRef<Record<string, LifePart>>(roles.objects);
    const plansRef = useRef<Record<string, DayPlan>>(plans.objects);
    const trackersRef = useRef<Record<string, Tracker>>(trackers.objects);
    const roleEfficiencySnapshotsRef = useRef<ObjectStorage<RoleEfficiencySnapshot>>(roleEfficiencySnapshots);
    const wheelId = useActualWheelId();

    useMemo(() => {
        // Обновляем данные в ссылках
        rolesRef.current = roles.objects;
        plansRef.current = plans.objects;
        trackersRef.current = trackers.objects;
        roleEfficiencySnapshotsRef.current = roleEfficiencySnapshots;
    }, [roles.objects, plans.objects, trackers.objects, roleEfficiencySnapshots, wheelId])

    const getEfficiency = useCallback(() => {
        const allRoles = Object.values(rolesRef.current);
        const scores: Record<string, number> = {};
        allRoles.forEach((part) => {
            scores[part.uuid] = computeRoleEfficiency(part, rolesRef.current, plansRef.current, trackersRef.current).score;
        });
        return scores;
    }, [wheelId]);

    const saveSnapshot = useCallback(() => {
        console.log('save eff snapshot for wheelId=', wheelId);
        const scores = getEfficiency();
        const today = format(new Date(), "yyyy-MM-dd");
        const existing = Object.values(roleEfficiencySnapshotsRef.current.objects).find(
            (s) => s.date === today,
        );
        if (existing) {
            console.log('eff update existed date=', today);

            roleEfficiencySnapshotsRef.current.save(existing.uuid, { scores, timestamp: Date.now() });
        } else {
            console.log('eff create new date=', today);

            roleEfficiencySnapshots.create({
                uuid: crypto.randomUUID(),
                wheelId,
                timestamp: Date.now(),
                date: today,
                scores,
            });
        }
    }, [getEfficiency, wheelId]);

    useEffect(() => {
        if (roleEfficiencySnapshots.isLoading) {
            return
        }
        saveSnapshot();
        const id = setInterval(saveSnapshot, INTERVAL_MS);
        return () => clearInterval(id);
    }, [saveSnapshot, roleEfficiencySnapshots.isLoading]);

    return null;
}
